import React, { useState, useEffect } from 'react';
import { ref, push, set, get, remove, update } from 'firebase/database';
import { database } from '../firebase/config';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PulsantiCondivisionePDF from './PulsantiCondivisionePDF';
import '../styles/Liste.css';
import { formattaData } from '../utils/dateUtils'; 
import { useFirebaseData } from '../hooks/useFirebaseData'; 

// Stili per il PDF
const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 11,
    fontFamily: 'Helvetica'
  },
  header: {
    fontSize: 20,
    marginBottom: 20,
    textAlign: 'center',
    color: '#2c3e50',
    fontWeight: 'bold'
  },
  section: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: '#f8f9fa',
    borderRadius: 5
  },
  label: {
    fontSize: 10,
    color: '#7f8c8d',
    marginBottom: 3
  },
  value: {
    fontSize: 12,
    color: '#2c3e50',
    marginBottom: 8,
    fontWeight: 'bold'
  },
  divider: {
    borderBottom: '1px solid #dee2e6',
    marginVertical: 10
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 9,
    color: '#95a5a6'
  }
});

// Componente PDF
export function ProgrammaPDF({ programma, dipendente, veicolo, cantiere, cliente }) {
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.header}>PROGRAMMA LAVORATIVO</Text>
        
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>DATA</Text>
          <Text style={pdfStyles.value}>{formattaData(programma.data)}</Text>
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>DIPENDENTE</Text>
          <Text style={pdfStyles.value}>{dipendente.nome} {dipendente.cognome}</Text>
          
          <Text style={pdfStyles.label}>ORARIO INIZIO</Text>
          <Text style={pdfStyles.value}>{programma.orarioInizio}</Text>
          
          <Text style={pdfStyles.label}>ORARIO FINE</Text>
          <Text style={pdfStyles.value}>{programma.orarioFine}</Text>
          
          <Text style={pdfStyles.label}>DURATA LAVORO</Text>
          <Text style={pdfStyles.value}>{programma.durataLavoro}</Text>
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>MEZZO UTILIZZATO</Text>
          <Text style={pdfStyles.value}>{veicolo.marca} {veicolo.modello} - Targa: {veicolo.targa}</Text>
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>CANTIERE</Text>
          <Text style={pdfStyles.value}>{cantiere.luogo}</Text>
          
          <Text style={pdfStyles.label}>CLIENTE</Text>
          <Text style={pdfStyles.value}>{cliente.ragioneSociale}</Text>
        </View>

        {programma.note && (
          <>
            <View style={pdfStyles.divider} />
            <View style={pdfStyles.section}>
              <Text style={pdfStyles.label}>NOTE</Text>
              <Text style={pdfStyles.value}>{programma.note}</Text>
            </View>
          </>
        )}

        <Text style={pdfStyles.footer}>
          Documento generato il {new Date().toLocaleDateString('it-IT')} - CRG Scavi App Cantieri
        </Text>
      </Page>
    </Document>
  );
}

// Componente PDF Lista Completa
function ListaProgrammiPDF({ programmi, dipendentiMap, veicoliMap, cantieriMap, clientiMap }) {
  // Raggruppa programmi per mese
  const gruppiPerMese = [];
  let meseCorrente = null;

  programmi.forEach(prog => {
    if (!prog.data) return;
    const [anno, mese] = prog.data.split('-');
    const chiave = `${anno}-${mese}`;

    if (chiave !== meseCorrente) {
      meseCorrente = chiave;
      const nomeMese = new Date(anno, parseInt(mese) - 1, 1)
        .toLocaleString('it-IT', { month: 'long', year: 'numeric' });
      gruppiPerMese.push({ tipo: 'mese', nomeMese });
    }

    gruppiPerMese.push({ tipo: 'riga', prog });
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={{
        padding: 30,
        fontSize: 10,
        fontFamily: 'Helvetica'
      }}>
        <Text style={{
          fontSize: 18,
          marginBottom: 15,
          textAlign: 'center',
          color: '#2c3e50',
          fontWeight: 'bold'
        }}>
          PROGRAMMI LAVORATIVI
        </Text>

        <View style={{ display: 'table', width: '100%', marginTop: 10 }}>
          {/* Header tabella */}
          <View style={{
            flexDirection: 'row',
            borderBottomWidth: 1,
            borderBottomColor: '#dee2e6',
            minHeight: 25,
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            fontSize: 9
          }}>
            <Text style={{ padding: 5, width: '10%' }}>Data</Text>
            <Text style={{ padding: 5, width: '17%' }}>Dipendente</Text>
            <Text style={{ padding: 5, width: '8%' }}>Inizio</Text>
            <Text style={{ padding: 5, width: '8%' }}>Fine</Text>
            <Text style={{ padding: 5, width: '8%' }}>Durata</Text>
            <Text style={{ padding: 5, width: '15%' }}>Mezzo</Text>
            <Text style={{ padding: 5, width: '17%' }}>Cliente</Text>
            <Text style={{ padding: 5, width: '17%' }}>Cantiere</Text>
          </View>

          {/* Righe con separatori mese */}
          {gruppiPerMese.map((item, index) => {
            if (item.tipo === 'mese') {
              return (
                <View key={`mese-${index}`} style={{
                  backgroundColor: '#2d2d2d',
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  marginTop: index > 0 ? 5 : 0,
                  marginBottom: 3
                }}>
                  <Text style={{
                    color: '#efd400',
                    fontWeight: 'bold',
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                     {item.nomeMese.charAt(0).toUpperCase() + item.nomeMese.slice(1)}
                  </Text>
                </View>
              );
            }

            const { prog } = item;
            const dipendente = dipendentiMap[prog.dipendenteId];
            const veicolo = veicoliMap[prog.veicoloId];
            const cantiere = cantieriMap[prog.cantiereId];
            const cliente = cantiere ? clientiMap[cantiere.clienteId] : null;

            return (
              <View key={`riga-${index}`} style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: '#dee2e6',
                minHeight: 25,
                alignItems: 'center'
              }}>
                <Text style={{ padding: 5, fontSize: 8, width: '10%' }}>{formattaData(prog.data)}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '17%' }}>
                  {dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}
                </Text>
                <Text style={{ padding: 5, fontSize: 8, width: '8%' }}>{prog.orarioInizio}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '8%' }}>{prog.orarioFine}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '8%' }}>{prog.durataLavoro}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '15%' }}>
                  {veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}
                </Text>
                <Text style={{ padding: 5, fontSize: 8, width: '17%' }}>
                  {cliente ? cliente.ragioneSociale : 'N/D'}
                </Text>
                <Text style={{ padding: 5, fontSize: 8, width: '17%' }}>
                  {cantiere ? cantiere.luogo : 'N/D'}
                </Text>
              </View>
            );
          })}
        </View>

        <Text style={{
          position: 'absolute',
          bottom: 30,
          left: 30,
          right: 30,
          textAlign: 'center',
          fontSize: 8,
          color: '#95a5a6'
        }}>
          Report generato il {new Date().toLocaleDateString('it-IT')} - CRG Scavi App Cantieri
        </Text>
      </Page>
    </Document>
  );
}

function ProgrammiLavoro() {
  const { dipendenti, veicoli, cantieri, clienti,
    caricaDipendenti, caricaVeicoli, caricaCantieri, caricaClienti
  } = useFirebaseData();
  const [programmi, setProgrammi] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    data: '',
    dipendenteId: '',
    orarioInizio: '',
    orarioFine: '',
    durataLavoro: '',
    veicoloId: '',
    cantiereId: '',
    note: ''
  });
  const [ordinamento, setOrdinamento] = useState({
    campo: 'data',
    direzione: 'desc'
  });

  useEffect(() => {
    caricaTuttiDati();
  }, []);

  async function caricaTuttiDati() {
    await Promise.all([
      caricaDipendenti(),
      caricaVeicoli(),
      caricaCantieri(),
      caricaClienti(),
      caricaProgrammi()
    ]);
  }


  async function caricaProgrammi() {
    const programmiRef = ref(database, 'programmi-lavorativi');
    const snapshot = await get(programmiRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      const programmiArray = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      setProgrammi(programmiArray);
    } else {
      setProgrammi([]);
    }
  }

  function apriModal(programma = null) {
    if (programma) {
      setEditingId(programma.id);
      setFormData({
        data: programma.data,
        dipendenteId: programma.dipendenteId,
        orarioInizio: programma.orarioInizio,
        orarioFine: programma.orarioFine,
        durataLavoro: programma.durataLavoro,
        veicoloId: programma.veicoloId,
        cantiereId: programma.cantiereId,
        note: programma.note || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        data: '',
        dipendenteId: '',
        orarioInizio: '',
        orarioFine: '',
        durataLavoro: '',
        veicoloId: '',
        cantiereId: '',
        note: ''
      });
    }
    setShowModal(true);
  }

  function chiudiModal() {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      data: '',
      dipendenteId: '',
      orarioInizio: '',
      orarioFine: '',
      durataLavoro: '',
      veicoloId: '',
      cantiereId: '',
      note: ''
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.data || !formData.dipendenteId || !formData.orarioInizio || 
        !formData.orarioFine || !formData.veicoloId || !formData.cantiereId) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

  // CONTROLLO: veicolo già occupato in quella fascia oraria
  const programmiStessaDataStessoVeicolo = programmi.filter(p =>
    p.data === formData.data &&
    p.veicoloId === formData.veicoloId &&
    p.id !== editingId // escludi il programma che stai modificando
  );

  const haConflitto = programmiStessaDataStessoVeicolo.some(p =>
    intervalliSiSovrappongono(
      formData.orarioInizio,
      formData.orarioFine,
      p.orarioInizio,
      p.orarioFine
    )
  );

  if (haConflitto) {
    alert('❌ Questo mezzo è già assegnato in quella fascia oraria per la stessa data.');
    return;
  }

    try {
      const dataToSave = {
        ...formData,
        durataLavoro: formData.durataLavoro || calcolaDurata(formData.orarioInizio, formData.orarioFine)
      };

      if (editingId) {
        const programmaRef = ref(database, `programmi-lavorativi/${editingId}`);
        await update(programmaRef, dataToSave);
        alert('Programma modificato con successo!');
      } else {
        const programmiRef = ref(database, 'programmi-lavorativi');
        const newProgrammaRef = push(programmiRef);
        await set(newProgrammaRef, {
          ...dataToSave,
          createdAt: Date.now()
        });
        alert('Programma creato con successo!');
      }
      
      chiudiModal();
      caricaProgrammi();
    } catch (error) {
      alert('Errore: ' + error.message);
    }
  }

  function calcolaDurata(inizio, fine) {
  const [h1, m1] = inizio.split(':').map(Number);
  const [h2, m2] = fine.split(':').map(Number);
  let minuti = (h2 * 60 + m2) - (h1 * 60 + m1);

  // Deduce 1 ora se l'intervallo comprende la fascia 12:00-13:00
  const inizioMinuti = h1 * 60 + m1;
  const fineMinuti = h2 * 60 + m2;
  if (inizioMinuti < 780 && fineMinuti > 720) {
    // 720 = 12:00, 780 = 13:00
    minuti -= 60;
  }

  const ore = Math.floor(minuti / 60);
  const min = minuti % 60;
  return `${ore}h${min > 0 ? ` ${min}m` : ''}`;
}

function intervalliSiSovrappongono(inizio1, fine1, inizio2, fine2) {
  const [h1, m1] = inizio1.split(':').map(Number);
  const [h2, m2] = fine1.split(':').map(Number);
  const [h3, m3] = inizio2.split(':').map(Number);
  const [h4, m4] = fine2.split(':').map(Number);

  const start1 = h1 * 60 + m1;
  const end1 = h2 * 60 + m2;
  const start2 = h3 * 60 + m3;
  const end2 = h4 * 60 + m4;

  // Sovrapposizione se l'intervallo 1 inizia prima che il 2 finisca
  // e finisce dopo che il 2 inizia
  return start1 < end2 && end1 > start2;
}


  async function eliminaProgramma(id, data) {
    if (window.confirm(`Sei sicuro di voler eliminare il programma del ${formattaData(data)}?`)) {
      try {
        const programmaRef = ref(database, `programmi-lavorativi/${id}`);
        await remove(programmaRef);
        alert('Programma eliminato!');
        caricaProgrammi();
      } catch (error) {
        alert('Errore: ' + error.message);
      }
    }
  }

  function getDipendente(id) {
    return dipendenti.find(d => d.id === id);
  }

  function getVeicolo(id) {
    return veicoli.find(v => v.id === id);
  }

  function getCantiere(id) {
    return cantieri.find(c => c.id === id);
  }

  function getCliente(cantiereId) {
    const cantiere = getCantiere(cantiereId);
    return cantiere ? clienti.find(c => c.id === cantiere.clienteId) : null;
  }

  function handleOrdinamento(campo) {
    let direzione = 'asc';
    
    if (ordinamento.campo === campo && ordinamento.direzione === 'asc') {
      direzione = 'desc';
    }
    
    setOrdinamento({ campo, direzione });
  }

  function getProgrammiOrdinati() {
    const programmiCopia = [...programmi];
    
    programmiCopia.sort((a, b) => {
      let valoreA, valoreB;
      
      if (ordinamento.campo === 'data') {
        valoreA = new Date(a.data);
        valoreB = new Date(b.data);
      } else if (ordinamento.campo === 'dipendente') {
        const dipA = getDipendente(a.dipendenteId);
        const dipB = getDipendente(b.dipendenteId);
        valoreA = dipA ? dipA.cognome.toLowerCase() : '';
        valoreB = dipB ? dipB.cognome.toLowerCase() : '';
      } else if (ordinamento.campo === 'cantiere') {
        const cantA = getCantiere(a.cantiereId);
        const cantB = getCantiere(b.cantiereId);
        valoreA = cantA ? cantA.luogo.toLowerCase() : '';
        valoreB = cantB ? cantB.luogo.toLowerCase() : '';
      }
      
      if (valoreA < valoreB) {
        return ordinamento.direzione === 'asc' ? -1 : 1;
      }
      if (valoreA > valoreB) {
        return ordinamento.direzione === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return programmiCopia;
  }

  function getProgrammiPerMese() {
    const ordinati = getProgrammiOrdinati();
    const gruppi = [];
    let meseCorrente = null;
    let dataCorrente = null;

ordinati.forEach(prog => {
  if (!prog.data) return;
  const [anno, mese] = prog.data.split('-');
  const chiave = `${anno}-${mese}`;
  if (chiave !== meseCorrente) {
    meseCorrente = chiave;
    const nomeMese = new Date(anno, parseInt(mese) - 1, 1).toLocaleString('it-IT', { month: 'long', year: 'numeric' });
    gruppi.push({ tipo: 'mese', chiave, nomeMese });
    dataCorrente = null; // reset al cambio mese
  }
  if (prog.data !== dataCorrente && dataCorrente !== null) {
    gruppi.push({ tipo: 'separatore' });
  }
gruppi.push({ tipo: 'riga', prog, primoDelGiorno: prog.data !== dataCorrente });
dataCorrente = prog.data;
});

    return gruppi;
  }

  function getFreccia(campo) {
    if (ordinamento.campo !== campo) {
      return '↕️';
    }
    return ordinamento.direzione === 'asc' ? '🔼' : '🔽';
  }

  const tuttiDatiPresenti = dipendenti.length > 0 && veicoli.length > 0 && cantieri.length > 0;

  return (
    <div className="lista-container">
      <div className="lista-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0 }}>Programmi Lavorativi</h2>
          {programmi.length > 0 && dipendenti.length > 0 && veicoli.length > 0 && cantieri.length > 0 && clienti.length > 0 && (
            <PulsantiCondivisionePDF
              documentoPDF={
                <ListaProgrammiPDF
                  programmi={getProgrammiOrdinati()}
                  dipendentiMap={dipendenti.reduce((acc, d) => ({ ...acc, [d.id]: d }), {})}
                  veicoliMap={veicoli.reduce((acc, v) => ({ ...acc, [v.id]: v }), {})}
                  cantieriMap={cantieri.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})}
                  clientiMap={clienti.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})}
                />
              }
              nomeFile="programmi_lavorativi.pdf"
              testoCondivisione="📋 Lista Programmi Lavorativi"
              oggettoEmail="Programmi Lavorativi"
              corpoEmail="In allegato la lista completa dei programmi lavorativi."
            />
          )}
        </div>
        <button 
          className="btn-add" 
          onClick={() => apriModal()}
          disabled={!tuttiDatiPresenti}
        >
          + Crea Programma
        </button>
      </div>

      {!tuttiDatiPresenti ? (
        <div className="empty-state">
          <p>⚠️ Per creare programmi lavorativi servono:</p>
          <ul style={{textAlign: 'left', display: 'inline-block'}}>
            <li>Almeno 1 Dipendente {dipendenti.length > 0 ? '✅' : '❌'}</li>
            <li>Almeno 1 Mezzo {veicoli.length > 0 ? '✅' : '❌'}</li>
            <li>Almeno 1 Cantiere {cantieri.length > 0 ? '✅' : '❌'}</li>
          </ul>
        </div>
      ) : programmi.length === 0 ? (
        <div className="empty-state">
          <p>Nessun programma lavorativo presente.</p>
          <p>Clicca su "Crea Programma" per iniziare.</p>
        </div>
      ) : (
        <>
          {/* Tabella Desktop */}
          <table className="data-table">
            <thead>
              <tr>
                <th 
                  onClick={() => handleOrdinamento('data')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                >
                  Data {getFreccia('data')}
                </th>
                <th 
                  onClick={() => handleOrdinamento('dipendente')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                >
                  Dipendente {getFreccia('dipendente')}
                </th>
                <th>Orari</th>
                <th 
                  onClick={() => handleOrdinamento('cantiere')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                >
                  Cantiere {getFreccia('cantiere')}
                </th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {getProgrammiPerMese().map((item, idx) => {
                if (item.tipo === 'mese') {
                  return (
                    <tr key={item.chiave} className="mese-header-row">
                      <td colSpan={5}>
                        📅 {item.nomeMese.charAt(0).toUpperCase() + item.nomeMese.slice(1)}
                      </td>
                    </tr>
                  );
                }

if (item.tipo === 'separatore') {
  return (
    <tr key={`sep-${Math.random()}`}>
      <td colSpan={5} style={{ background: 'transparent', border: 'none' }}></td>
    </tr>
  );
}


                const { prog } = item;
                const dipendente = getDipendente(prog.dipendenteId);
                const veicolo = getVeicolo(prog.veicoloId);
                const cantiere = getCantiere(prog.cantiereId);
                const cliente = getCliente(prog.cantiereId);

                return (
                  <tr key={prog.id} className={item.primoDelGiorno ? 'primo-del-giorno' : ''}>
                    <td><strong>{formattaData(prog.data)}</strong></td>
                    <td>{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}</td>
                    <td>{prog.orarioInizio} - {prog.orarioFine}<br/><small>({prog.durataLavoro})</small></td>
                    <td>{cantiere ? cantiere.luogo : 'N/D'}</td>
                    <td className="actions">
                      {dipendente && veicolo && cantiere && cliente && (
<PulsantiCondivisionePDF
  documentoPDF={
    <ProgrammaPDF 
      programma={prog}
      dipendente={dipendente}
      veicolo={veicolo}
      cantiere={cantiere}
      cliente={cliente}
    />
  }
  nomeFile={`programma_${prog.data}_${dipendente.cognome}.pdf`}
  testoCondivisione={`📋 Programma Lavorativo ${formattaData(prog.data)}\\n\\n 👤 ${dipendente.nome} ${dipendente.cognome}\n🏗️ ${cantiere.luogo}\n🏢 ${cliente.ragioneSociale}\n⏰ ${prog.orarioInizio} - ${prog.orarioFine}\n📝 ${prog.note || 'Nessuna nota'}`}
  emailDestinatario=""
  oggettoEmail={`Programma Lavorativo - ${prog.data} - ${dipendente.cognome}`}
  corpoEmail={`Ciao,\n\nallego il programma lavorativo per la data ${formattaData(prog.data)}.\n\nDIPENDENTE: ${dipendente.nome} ${dipendente.cognome}\nCANTIERE: ${cantiere.luogo}\nCLIENTE: ${cliente.ragioneSociale}\nMEZZO: ${veicolo.marca} ${veicolo.modello} (${veicolo.targa})\nORARIO: ${prog.orarioInizio} - ${prog.orarioFine}\nDURATA: ${prog.durataLavoro}\n\nNote: ${prog.note || 'Nessuna nota'}\n\nCordiali saluti`}
/>

                      )}
                      <button 
                        className="btn-edit"
                        onClick={() => apriModal(prog)}
                      >
                        <img src="/icon-edit.svg" alt="pdf" style={{ width: '40px' }} />
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => eliminaProgramma(prog.id, prog.data)}
                      >
                        <img src="/icon-delete.svg" alt="pdf" style={{ width: '40px' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Layout Card Mobile */}
          <div className="mobile-cards">
            {getProgrammiOrdinati().map(prog => {
              const dipendente = getDipendente(prog.dipendenteId);
              const veicolo = getVeicolo(prog.veicoloId);
              const cantiere = getCantiere(prog.cantiereId);
              const cliente = getCliente(prog.cantiereId);

              return (
                <div key={prog.id} className="mobile-card">
                  <div className="mobile-card-header">
                    📅 {formattaData(prog.data)} - 👤 {dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}
                  </div>
                  
                  <div style={{
                    background: '#e3f2fd',
                    padding: '10px',
                    borderRadius: '5px',
                    marginBottom: '10px',
                    textAlign: 'center'
                  }}>
                    <strong style={{fontSize: '16px'}}>
                      ⏰ {prog.orarioInizio} → {prog.orarioFine}
                    </strong>
                    <div style={{color: '#666', fontSize: '13px', marginTop: '5px'}}>
                      Durata: {prog.durataLavoro}
                    </div>
                  </div>
                  
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🏗️ Cantiere:</span>
                    <span className="mobile-card-value">
                      <strong>{cantiere ? cantiere.luogo : 'N/D'}</strong>
                    </span>
                  </div>
                  
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🏢 Cliente:</span>
                    <span className="mobile-card-value">
                      {cliente ? cliente.ragioneSociale : 'N/D'}
                    </span>
                  </div>
                  
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🚗 Mezzo:</span>
                    <span className="mobile-card-value">
                      {veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}
                    </span>
                  </div>
                  
                  {prog.note && (
                    <div style={{
                      background: '#fff3cd',
                      padding: '8px',
                      borderRadius: '5px',
                      marginTop: '10px',
                      fontSize: '13px'
                    }}>
                      <strong>📝 Note:</strong> {prog.note}
                    </div>
                  )}
                  
                  <div className="mobile-card-actions">
                    {dipendente && veicolo && cantiere && cliente && (
<PulsantiCondivisionePDF
  documentoPDF={
    <ProgrammaPDF 
      programma={prog}
      dipendente={dipendente}
      veicolo={veicolo}
      cantiere={cantiere}
      cliente={cliente}
    />
  }
  nomeFile={`programma_${prog.data}_${dipendente.cognome}.pdf`}
  testoCondivisione={`📋 Programma Lavorativo ${prog.data}\n\n👤 ${dipendente.nome} ${dipendente.cognome}\n🏗️ ${cantiere.luogo}\n🏢 ${cliente.ragioneSociale}\n⏰ ${prog.orarioInizio} - ${prog.orarioFine}\n📝 ${prog.note || 'Nessuna nota'}`}
  emailDestinatario=""
  oggettoEmail={`Programma Lavorativo - ${prog.data} - ${dipendente.cognome}`}
  corpoEmail={`Ciao,\n\nallego il programma lavorativo per la data ${prog.data}.\n\nDIPENDENTE: ${dipendente.nome} ${dipendente.cognome}\nCANTIERE: ${cantiere.luogo}\nCLIENTE: ${cliente.ragioneSociale}\nMEZZO: ${veicolo.marca} ${veicolo.modello} (${veicolo.targa})\nORARIO: ${prog.orarioInizio} - ${prog.orarioFine}\nDURATA: ${prog.durataLavoro}\n\nNote: ${prog.note || 'Nessuna nota'}\n\nCordiali saluti`}
/>

                    )}
                    <button 
                      className="btn-edit"
                      onClick={() => apriModal(prog)}
                    >
                      ✏️ Modifica
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => eliminaProgramma(prog.id, prog.data)}
                    >
                      <img src="/icon-delete.svg" alt="pdf" style={{ width: '40px' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={chiudiModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <h3>{editingId ? 'Modifica Programma' : 'Nuovo Programma'}</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Data *</label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Dipendente *</label>
                <select
                  value={formData.dipendenteId}
                  onChange={(e) => setFormData({...formData, dipendenteId: e.target.value})}
                  required
                >
                  <option value="">-- Seleziona dipendente --</option>
                  {dipendenti.map(dip => (
                    <option key={dip.id} value={dip.id}>
                      {dip.nome} {dip.cognome}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px'}}>
                <div className="form-group">
                  <label>Orario Inizio *</label>
                  <input
                    type="time"
                    value={formData.orarioInizio}
                    onChange={(e) => setFormData({...formData, orarioInizio: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Orario Fine *</label>
                  <input
                    type="time"
                    value={formData.orarioFine}
                    onChange={(e) => setFormData({...formData, orarioFine: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Durata</label>
                  <input
                    type="text"
                    value={formData.durataLavoro}
                    onChange={(e) => setFormData({...formData, durataLavoro: e.target.value})}
                    placeholder="Auto"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Mezzo Utilizzato *</label>
                <select
                  value={formData.veicoloId}
                  onChange={(e) => setFormData({...formData, veicoloId: e.target.value})}
                  required
                >
                  <option value="">-- Seleziona mezzo --</option>
                  {veicoli.map(veic => (
                    <option key={veic.id} value={veic.id}>
                      {veic.marca} {veic.modello} - {veic.targa}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Cantiere *</label>
                <select
                  value={formData.cantiereId}
                  onChange={(e) => setFormData({...formData, cantiereId: e.target.value})}
                  required
                >
                  <option value="">-- Seleziona cantiere --</option>
                  {cantieri.map(cant => {
                    const cliente = clienti.find(c => c.id === cant.clienteId);
                    return (
                      <option key={cant.id} value={cant.id}>
                        {cant.luogo} ({cliente ? cliente.ragioneSociale : ''})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="form-group">
                <label>Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Note aggiuntive..."
                  rows="3"
                />
              </div>

              <div className="modal-buttons">
                <button type="button" className="btn-cancel" onClick={chiudiModal}>
                  Annulla
                </button>
                <button type="submit" className="btn-save">
                  {editingId ? 'Salva Modifiche' : 'Crea Programma'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgrammiLavoro;